import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { getDomain, getProgramsByDomain, programDomains, programTemplates } from '../data/programTemplates';
import { LegalFooter } from '../components/LegalFooter';
import { HeroMatchForm } from '../components/HeroMatchForm';
import { usePageMeta } from '../hooks/usePageMeta';
export function ProgramDomainsPage() {
  usePageMeta('New Online Programs | Six Strategic Domains', 'Explore new online healthcare and business programs organized into six strategic domains — from clinical care and healthcare operations to data, community health, and leadership.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  return <main className="min-h-screen bg-white" data-id="element-2236" data-mp="vmher0qt1nnk">
      {/* Hero */}
      <section className="bg-navy" data-id="element-2237" data-mp="1kvgtehx996wq">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-id="element-2238" data-mp="1kwi1cdiz5da8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-2239" data-mp="1owk7ayvqssvb">
            <div className="lg:col-span-3" data-id="element-2240" data-mp="1ayc449844vbp">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-2241" data-mp="1axkhhnwhu7xk">
                {programTemplates.length} Programs · {programDomains.length}{' '}
                Domains
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6" data-id="element-2242" data-mp="1166c4zhv2dwi">
                New Online Programs
              </h1>
              <p className="text-lg text-white/80 leading-relaxed" data-id="element-2243" data-mp="s8dvns0el07j">
                New online programs are organized into six strategic domains
                that reflect key workforce needs across healthcare and related
                business functions. This structure clarifies how each program
                aligns to a broader capability area — from direct care support
                and healthcare operations to data, community health, and
                leadership.
              </p>
            </div>

            <div id="hero-match-form" className="lg:col-span-2" data-id="element-2244" data-mp="3vgpq1ux0azig">
              <HeroMatchForm programs={programTemplates} domainNameFor={program => getDomain(program.domainSlug)?.name} data-id="element-2245" data-mp="3qio85dbbh68k" />
            </div>
          </div>
        </div>
      </section>

      {/* Domains */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14" data-id="element-2246" data-mp="n0eg0r7ednx6">
        {programDomains.map((domain, idx) => {
        const programs = getProgramsByDomain(domain.slug);
        return <section key={domain.slug} aria-labelledby={`domain-${domain.slug}`} data-id="element-2247" data-mp="d64bkb56kz63" data-mpi={idx}>
              <div className="flex items-baseline gap-4 mb-3" data-id="element-2248" data-mp="84yjmfjchfxm">
                <span className="text-sm font-bold tabular-nums text-crimson" data-id="element-2249" data-mp="2xvlb6pmah3kf">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h2 id={`domain-${domain.slug}`} className="text-2xl sm:text-3xl font-bold text-navy" data-id="element-2250" data-mp="2sxs47ud5w5j3">
                  {domain.name}
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed max-w-3xl mb-6 pl-9" data-id="element-2251" data-mp="1ib2jxr1fuzmx">
                {domain.description}
              </p>
              <ul className="grid sm:grid-cols-2 gap-4 pl-9" data-id="element-2252" data-mp="1n3vpgh4qec02">
                {programs.map(program => <li key={program.slug} className="flex" data-id="element-2253" data-mp="1s14aqql6ew5j">
                    <Link to={`/program-information/${program.slug}`} className="group border border-gray-200 rounded-xl p-5 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-2254" data-mp="1w27nh47xgc2j">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-2255" data-mp="25ysrpnc4hj5b">
                        {program.credential}
                      </span>
                      <span className="font-bold text-navy mb-2" data-id="element-2256" data-mp="bp8epxk8c5ik">
                        {program.name}
                      </span>
                      <span className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-2257" data-mp="1x5o9fb48h95i">
                        {program.weeks} weeks · {program.credits} credits ·{' '}
                        {program.instructionalHours.toLocaleString()} hours
                      </span>
                      <span className="mt-auto inline-flex items-center text-sm font-semibold text-crimson" data-id="element-2258" data-mp="271hujiw0qayw">
                        View program
                        <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-2259" data-mp="1efgdpe2v5kzc" />
                      </span>
                    </Link>
                  </li>)}
              </ul>
            </section>;
      })}
      </div>

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2260" data-mp="vne3akgtf0xj">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2261" data-mp="lk8r7qws6rku">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2262" data-mp="lixkbi81h3pi">
            Find the Pathway That Fits Your Goals
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2263" data-mp="bm2t7ke3i5l9">
            Get matched with accredited schools offering these programs. Free,
            and no obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2264" data-mp="git4zvivaf0v">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" data-id="element-2265" data-mp="148ngo9rg9xjj">
              Get Matched Free
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2266" data-mp="3f2roubc53uw5" />
            </button>
            <Link to="/request-information" className="text-sm font-semibold text-white/80 hover:text-white underline underline-offset-4 transition-colors duration-200" data-id="element-2267" data-mp="3f54df7cel3i6">
              Or request information
            </Link>
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-2268" data-mp="1gtc95pikpsoc" />
    </main>;
}